import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import type { Tag } from '@wml-crm/shared';

interface Props {
  modelId: string;
  tags: { id: string; name: string }[];
  onChange: (tags: { id: string; name: string }[]) => void;
}

/**
 * Блок тегов на странице модели. Показывает текущие теги (с крестиком),
 * кнопка «+ тег» открывает поповер: все существующие + инпут для нового.
 * При выборе / создании — сразу пуляем POST + обновляем родителя.
 */
export function TagsBlock({ modelId, tags, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Tag | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    api<Tag[]>('/api/tags').then(setAllTags).catch(() => setAllTags([]));
    const onDoc = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  async function attach(payload: { tagId?: string; name?: string }) {
    if (busy) return;
    setBusy(true);
    try {
      const created = await api<{ id: string; name: string }>(`/api/models/${modelId}/tags`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!tags.some((t) => t.id === created.id)) onChange([...tags, created].sort((a, b) => a.name.localeCompare(b.name, 'ru')));
      setNewName('');
      setOpen(false);
      // обновляем локальный кэш всех тегов на всякий
      if (!allTags.some((t) => t.id === created.id)) {
        setAllTags([...allTags, { id: created.id, name: created.name, createdAt: '' }]);
      }
    } catch {
      /* игнор */
    } finally {
      setBusy(false);
    }
  }

  async function detach(tagId: string) {
    try {
      await api(`/api/models/${modelId}/tags/${tagId}`, { method: 'DELETE' });
      onChange(tags.filter((t) => t.id !== tagId));
    } catch { /* игнор */ }
  }

  async function deleteFromLibrary() {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api(`/api/tags/${deleting.id}`, { method: 'DELETE' });
      setAllTags(allTags.filter((t) => t.id !== deleting.id));
      // Если этот тег был на модели — уходит и оттуда (каскад в БД удалил связку).
      if (tags.some((t) => t.id === deleting.id)) {
        onChange(tags.filter((t) => t.id !== deleting.id));
      }
      setDeleting(null);
    } catch { /* игнор */ }
    finally { setDeleteBusy(false); }
  }

  const currentIds = new Set(tags.map((t) => t.id));
  const available = allTags
    .filter((t) => !currentIds.has(t.id))
    .filter((t) => !newName.trim() || t.name.toLowerCase().includes(newName.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <div className="tags-block">
      <div className="tags-title">Теги</div>
      <div className="tags-list">
        {tags.map((t) => (
          <span key={t.id} className="tag-chip">
            <span>{t.name}</span>
            <button
              type="button"
              className="tag-chip-x"
              title="Убрать тег с модели"
              onClick={() => detach(t.id)}
            >×</button>
          </span>
        ))}
        <div className="tag-picker-wrap">
          <button type="button" className="tag-add-btn" onClick={() => setOpen((v) => !v)}>
            + тег
          </button>
          {open && (
            <div className="tag-popover" ref={popRef}>
              <input
                autoFocus
                type="text"
                placeholder="Найти или создать новый…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newName.trim()) {
                    e.preventDefault();
                    attach({ name: newName.trim() });
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                  }
                }}
              />
              <div className="tag-popover-list">
                {available.length === 0 && !newName.trim() && (
                  <div className="tag-popover-empty">Тегов ещё нет. Введи имя и Enter.</div>
                )}
                {available.map((t) => (
                  <div key={t.id} className="tag-popover-row">
                    <button
                      type="button"
                      className="tag-popover-item"
                      onClick={() => attach({ tagId: t.id })}
                    >
                      {t.name}
                      {typeof t.modelCount === 'number' && (
                        <span className="tag-popover-count">{t.modelCount}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="tag-popover-delete"
                      title="Удалить тег из списка"
                      onClick={(e) => { e.stopPropagation(); setDeleting(t); }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>
                  </div>
                ))}
                {newName.trim() && !available.some((t) => t.name.toLowerCase() === newName.trim().toLowerCase()) && (
                  <button
                    type="button"
                    className="tag-popover-item tag-popover-create"
                    onClick={() => attach({ name: newName.trim() })}
                  >
                    + создать «{newName.trim()}»
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {deleting && (
        <ConfirmDialog
          title="Удалить тег из списка?"
          message={
            <>
              Тег <b>«{deleting.name}»</b> будет удалён из общего списка{typeof deleting.modelCount === 'number' && deleting.modelCount > 0 ? (
                <> и снят с <b>{deleting.modelCount}</b> {deleting.modelCount === 1 ? 'модели' : deleting.modelCount < 5 ? 'моделей' : 'моделей'}</>
              ) : null}.
              <br />
              Это действие нельзя отменить.
            </>
          }
          confirmLabel="Удалить"
          danger
          busy={deleteBusy}
          onConfirm={deleteFromLibrary}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
